import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Compass, Target, TrendingUp, Sparkles, MessageCircle, CheckCircle, Gem, Lightbulb, UserRound } from "lucide-react";

const pillars = [
  {
    icon: Compass,
    title: "Find Your Direction",
    description:
      "Feeling stuck in your career or business? We get clear on where you actually want to go, and the real reasons you haven't gotten there yet.",
  },
  {
    icon: Target,
    title: "Build a Real Plan",
    description:
      "No fluff, no vision boards. Concrete weekly actions tied to outcomes that move your career or revenue forward.",
  },
  {
    icon: TrendingUp,
    title: "Sales & Performance",
    description:
      "Drawing on 10+ years in sales, confidence on calls, closing more deals, negotiating better offers, and leading high-performing teams.",
  },
  {
    icon: Sparkles,
    title: "Mindset That Sticks",
    description:
      "Imposter syndrome, burnout, fear of pitching, perfectionism. We work on the patterns underneath, not just the surface symptoms.",
  },
];

const whoFor = [
  "Sales reps and AEs who want to break into the top 10%",
  "Founders and small business owners feeling overwhelmed or stuck",
  "Mid-career professionals planning a pivot or promotion",
  "Sales leaders coaching their first team",
  "Anyone who knows they're capable of more but can't see the next move",
];

const faqs = [
  { q: "Who is this coaching for?", a: "Founders, sales reps, sales leaders, and mid-career professionals who feel stuck, are planning a pivot or promotion, or want to break into the top 10% of their field." },
  { q: "How are sessions structured?", a: "Weekly 1:1 calls (60 minutes) with concrete homework between sessions. Most engagements run 3, 6 months, but you can stop anytime, no long-term lock-in." },
  { q: "What makes this different from other business coaching?", a: "Real sales experience, not classroom theory. 10+ years in the trenches plus a formal coaching certification. We focus on outcomes you can measure, pipeline, close rate, promotion, revenue, not just feeling better." },
  { q: "Do you offer a free intro call?", a: "Yes. A free 30-minute discovery call to see if it's the right fit. Zero pressure, if it's not a match, you'll leave with at least one useful next step." },
  { q: "How much does coaching cost?", a: "Pricing depends on engagement length and intensity. We discuss it on the discovery call once we know what you actually need." },
];

const Coaching = () => {
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Business & Career Coaching",
    serviceType: ["Business Coaching", "Career Coaching", "Sales Coaching"],
    areaServed: { "@type": "Country", name: "United States" },
    provider: { "@id": "https://www.elevateglobalsolutions.net/#organization" },
    description:
      "1:1 business and career coaching for professionals and entrepreneurs ready to level up. Led by a certified coach with 10+ years in sales.",
  };
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://www.elevateglobalsolutions.net/" },
      { "@type": "ListItem", position: 2, name: "Coaching", item: "https://www.elevateglobalsolutions.net/coaching" },
    ],
  };
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <>
      <Helmet>
        <title>Business & Career Coaching for Founders & Sales Pros</title>
        <meta
          name="description"
          content="1:1 business and career coaching for founders, sales reps, and professionals. Certified coach + 10 years in sales. Get unstuck, build a plan, hit your next level."
        />
        <meta
          name="keywords"
          content="business coaching, career coaching, sales coaching, executive coaching, 1 on 1 coaching, coaching for founders, coaching for entrepreneurs, sales performance coaching, career pivot coaching, mindset coaching, leadership coaching, coaching for sales reps, certified business coach, coaching for small business owners"
        />
        <meta property="og:title" content="Business & Career Coaching | Elevate Global Solutions" />
        <meta property="og:description" content="Personal coaching for professionals and entrepreneurs who feel stuck. Sales expertise + certified coaching." />
        <meta property="og:url" content="https://www.elevateglobalsolutions.net/coaching" />
        <meta property="og:image" content="https://www.elevateglobalsolutions.net/og-default.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://www.elevateglobalsolutions.net/og-default.jpg" />
        <meta name="twitter:title" content="Business & Career Coaching | Elevate Global Solutions" />
        <meta name="twitter:description" content="Personal coaching for professionals and entrepreneurs who feel stuck." />
        <link rel="canonical" href="https://www.elevateglobalsolutions.net/coaching" />
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>


      {/* Hero */}
      <section className="bg-primary section-padding py-20 md:py-28">
        <div className="container-narrow text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-primary-foreground/20 bg-primary-foreground/10 px-3 py-1 text-xs font-medium text-primary-foreground/90">
            <MessageCircle size={14} />
            1:1 Coaching
          </span>
          <h1 className="mt-5 text-4xl font-extrabold leading-tight text-primary-foreground md:text-5xl">
            Stuck in Your Career or Business? Let's Get You Moving.
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-lg text-primary-foreground/80">
            Personal business and career coaching for professionals who feel stuck, overwhelmed, or
            ready for the next level, but aren't sure what that move actually looks like.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/book">
              <Button
                size="lg"
                className="bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-lg shadow-secondary/30"
              >
                Book a Free Discovery Call
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button
                size="lg"
                variant="outline"
                className="border-primary-foreground bg-primary-foreground text-primary hover:bg-primary-foreground/90"
              >
                Ask a Question
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why coaching */}
      <section className="section-padding">
        <div className="container-narrow">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-3xl font-extrabold text-foreground md:text-4xl">
              You Are the Best Investment You Will Ever Make
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              You can hire skills. You cannot hire your clarity, your confidence, or your next level.
              That is why we invest in you.
            </p>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-3">
            <div className="rounded-xl border border-border bg-card p-6 text-center">
              <div className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-accent text-primary">
                <Gem size={22} />
              </div>
              <h3 className="text-lg font-bold text-foreground">The Real Gold Is Inside You</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Systems break. Tools expire. But your mindset, your focus, and your decisions compound forever. That is where your real leverage lives.
              </p>
            </div>
            <div className="rounded-xl border border-border bg-card p-6 text-center">
              <div className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-accent text-primary">
                <Lightbulb size={22} />
              </div>
              <h3 className="text-lg font-bold text-foreground">It Is Not a Talent Problem</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                After ten years in sales, I have watched smart people plateau and average people soar. The difference is never IQ. It is direction.
              </p>
            </div>
            <div className="rounded-xl border border-border bg-card p-6 text-center">
              <div className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-accent text-primary">
                <UserRound size={22} />
              </div>
              <h3 className="text-lg font-bold text-foreground">From the Trenches, Not a Textbook</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Ten years of real sales battles plus a formal coaching certification. I have lived what you are going through, and I have the framework to get you out.
              </p>
            </div>
          </div>

          <div className="mx-auto mt-12 max-w-2xl text-center text-muted-foreground leading-relaxed">
            <p>
              This is not corporate fluff. This is a belief built from watching great people get stuck for fixable reasons. If you are ready to move, the work starts with a conversation.
            </p>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="section-padding bg-accent/50">
        <div className="container-narrow">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-foreground md:text-4xl">
              What We Work On Together
            </h2>
            <p className="mt-3 text-muted-foreground">
              Practical, personal, and focused on outcomes you can feel.
            </p>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {pillars.map((pillar) => (
              <div
                key={pillar.title}
                className="rounded-xl border border-border bg-card p-6 transition-all duration-300 hover:border-primary hover:shadow-lg hover:shadow-primary/5"
              >
                <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-lg bg-accent text-primary">
                  <pillar.icon size={24} />
                </div>
                <h3 className="text-lg font-semibold text-foreground">{pillar.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{pillar.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who it's for */}
      <section className="section-padding">
        <div className="container-narrow">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-3xl font-bold text-foreground md:text-4xl">Who This Is For</h2>
            <ul className="mt-6 space-y-3">
              {whoFor.map((item) => (
                <li key={item} className="flex items-start gap-3 text-muted-foreground">
                  <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-secondary" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding">
        <div className="container-narrow">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-3xl font-bold text-foreground md:text-4xl">Common Questions</h2>
            <div className="mt-8 space-y-4">
              {faqs.map((f) => (
                <details key={f.q} className="rounded-xl border border-border bg-card p-5">
                  <summary className="cursor-pointer text-base font-semibold text-foreground [&::-webkit-details-marker]:hidden">
                    {f.q}
                  </summary>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}

      <section className="section-padding bg-accent/50">
        <div className="container-narrow text-center">
          <h2 className="text-3xl font-bold text-foreground">
            Your Next Move Starts With a Conversation
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
            Book a free discovery call. We'll talk through where you are, where you want to be, and
            whether coaching is the right fit, zero pressure.
          </p>
          <Link to="/book" className="mt-8 inline-block">
            <Button
              size="lg"
              className="bg-secondary text-secondary-foreground hover:bg-secondary/90"
            >
              Book My Free Discovery Call
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </>
  );
};

export default Coaching;
